import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";

import IconButton from "@material-ui/core/IconButton";
import { ArrowLeft, ContactRound, Plus, Upload, Pencil, Trash2, CheckCircle2, Ban } from "lucide-react";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import { useSearch } from "../../context/Search/SearchContext";
// import { SocketContext } from "../../context/Socket/SocketContext";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      ...(theme.scrollbarStyles || {}),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
    },
    backBtn: {
      borderRadius: 10,
      padding: 6,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    titleWrap: {
      minWidth: 0,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      maxWidth: "100%",
    },
    titleText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      flexShrink: 0,
    },
    pageSubtitle: {
      fontSize: 12,
      color: dsColors.textSecondary || (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
      flexWrap: "wrap",
    },
    newBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    ghostBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 16px",
      gap: 6,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      background: "transparent",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      },
    },
    mainPaper: {
      flex: 1,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.35)" : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      overflowY: "auto",
      overflowX: "auto",
      ...(theme.scrollbarStyles || {}),
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
        whiteSpace: "nowrap",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
    },
    validityIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      borderRadius: 999,
    },
    validIcon: {
      background: "rgba(16,185,129,0.12)",
      color: "#10b981",
    },
    invalidIcon: {
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    actionsCell: {
      whiteSpace: "nowrap",
      "& > button": { marginLeft: 4 },
      "& > button:first-child": { marginLeft: 0 },
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: "#e53935",
      "&:hover": {
        background: "rgba(229,57,53,0.10)",
        color: "#c62828",
      },
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontSize: 13.5,
    },
  };
});

const ContactListItems = () => {
  const classes = useStyles();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();
  const { ticketSearchParam, clear } = useSearch();
  const searchParam = (ticketSearchParam || "").toLowerCase();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    }
    socket.on(`company-${companyId}-ContactListItem`, onCompanyContactLists);

    return () => {
      socket.off(`company-${companyId}-ContactListItem`, onCompanyContactLists);
    };
  }, [contactListId]);

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name
            }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={planilhaExemplo} download="planilha.xlsx">
              {i18n.t("contactListItems.downloadTemplate")}
            </a>
          </>
        )}
      </ConfirmationModal>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <IconButton
            size="small"
            className={classes.backBtn}
            onClick={goToContactLists}
            title={i18n.t("contactListItems.buttons.lists")}
          >
            <ArrowLeft size={18} strokeWidth={2} />
          </IconButton>
          <span className={classes.pageIconWrap}>
            <ContactRound size={20} strokeWidth={1.8} />
          </span>
          <div className={classes.titleWrap}>
            <div className={classes.pageTitle}>
              <span className={classes.titleText}>{contactList.name || i18n.t("contactListItems.title")}</span>
              <span className={classes.countChip}>{contacts.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("contactListItems.subtitle")}
            </div>
          </div>
        </div>

        <div className={classes.headerActions}>
          <Button
            className={classes.ghostBtn}
            onClick={() => {
              fileUploadRef.current.value = null;
              fileUploadRef.current.click();
            }}
          >
            <Upload size={16} strokeWidth={2} />
            {i18n.t("contactListItems.buttons.import")}
          </Button>
          <Button className={classes.newBtn} onClick={handleOpenContactListItemModal}>
            <Plus size={16} strokeWidth={2} />
            {i18n.t("contactListItems.buttons.add")}
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
        <input
          style={{ display: "none" }}
          id="upload"
          name="file"
          type="file"
          accept=".xls,.xlsx"
          onChange={() => {
            setConfirmOpen(true);
          }}
          ref={fileUploadRef}
        />
        <div className={classes.tableScroll} onScroll={handleScroll}>
          <Table size="small" className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="center" style={{ width: "0%" }}>#</TableCell>
                <TableCell align="left">{i18n.t("contactListItems.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("contactListItems.table.number")}</TableCell>
                <TableCell align="center">{i18n.t("contactListItems.table.email")}</TableCell>
                <TableCell align="right">{i18n.t("contactListItems.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && contacts.length === 0 ? (
                <TableRowSkeleton columns={5} />
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyState}>
                    {searchParam
                      ? i18n.t("contactListItems.empty.search")
                      : i18n.t("contactListItems.empty.list")}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id} className={classes.tableRow}>
                      <TableCell align="center" style={{ width: "0%" }}>
                        <span
                          className={`${classes.validityIcon} ${
                            contact.isWhatsappValid ? classes.validIcon : classes.invalidIcon
                          }`}
                          title={i18n.t(contact.isWhatsappValid ? "contactListItems.whatsappValid" : "contactListItems.whatsappInvalid")}
                        >
                          {contact.isWhatsappValid ? (
                            <CheckCircle2 size={16} strokeWidth={2} />
                          ) : (
                            <Ban size={16} strokeWidth={2} />
                          )}
                        </span>
                      </TableCell>
                      <TableCell align="left" className={classes.nameCell}>{contact.name}</TableCell>
                      <TableCell align="center">{contact.number}</TableCell>
                      <TableCell align="center">{contact.email}</TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <IconButton
                          size="small"
                          className={classes.iconActionBtn}
                          onClick={() => hadleEditContact(contact.id)}
                        >
                          <Pencil size={16} strokeWidth={1.8} />
                        </IconButton>
                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <IconButton
                              size="small"
                              className={classes.deleteActionBtn}
                              onClick={() => {
                                setConfirmOpen(true);
                                setDeletingContact(contact);
                              }}
                            >
                              <Trash2 size={16} strokeWidth={1.8} />
                            </IconButton>
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={5} />}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>
    </div>
  );
};

export default ContactListItems;
